// components/FamilyChartPane.jsx
// React wrapper around the family-chart D3 library.
// Renders an interactive, zoomable genogram in the top-right pane.

import React from 'react';
import { RELATION_CONFIG, FATE_CONFIG } from '../constants/family';
import { Z } from '../constants/tokens';

// family-chart library (local build)
import * as f3 from 'family-chart';
import 'family-chart/styles/family-chart.css';

// ── Default card style settings (persisted to localStorage) ──
const SETTINGS_KEY = 'gwv_genogram_card_settings';

const DEFAULT_SETTINGS = {
  cardWidth: 170,
  cardHeight: 80,
  cardSpacingX: 240,
  cardSpacingY: 160,
  cardRadius: 10,
  cardPadding: 8,
  nameFontSize: 0.74,
  relationFontSize: 0.52,
  dateFontSize: 0.6,
  fateFontSize: 0.52,
  showFateBadge: true,
  showDates: true,
  showRelationLabel: true,
  subjectHighlight: '#f5a623',
  maleColor: '#789fac',
  femaleColor: '#c48a92',
  // Per-fate color overrides (null = use defaults from FATE_CONFIG)
  fateSurvivedColor: null,
  fateKilledColor: null,
  fateEmigratedColor: null,
  // Connector line settings
  lineColor: '#888888',
  lineWidth: 1,
};

// The settings modal's swatch for these three used to show grey #888888 while
// the chart actually drew the FATE_CONFIG colour, so the control contradicted
// the cards it was meant to describe.
const FATE_COLOR_DEFAULTS = {
  fateSurvivedColor:  FATE_CONFIG.survived.color,
  fateKilledColor:    FATE_CONFIG.killed.color,
  fateEmigratedColor: FATE_CONFIG.emigrated.color,
};

function loadSettings() {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    return raw ? { ...DEFAULT_SETTINGS, ...JSON.parse(raw) } : { ...DEFAULT_SETTINGS };
  } catch { return { ...DEFAULT_SETTINGS }; }
}
function saveSettings(s) {
  localStorage.setItem(SETTINGS_KEY, JSON.stringify(s));
  window.dispatchEvent(new Event('genogram-settings-changed'));
}


export default function FamilyChartPane({
  data,
  theme,
  isDarkMode,
  onPersonClick,
  selectedId,
  subjectName,
  cardSettings: externalSettings,
  memberGroupMap = {},
  activeGroupFilter = null,
  onKinships = null,
}) {
  const contRef = React.useRef(null);
  const chartRef = React.useRef(null);
  const cardRef = React.useRef(null);
  const initializedRef = React.useRef(false);

  // Live-updating settings: listen for custom event from the settings modal
  const [settings, setSettings] = React.useState(() => externalSettings || loadSettings());
  React.useEffect(() => {
    const handler = () => setSettings(loadSettings());
    window.addEventListener('genogram-settings-changed', handler);
    return () => window.removeEventListener('genogram-settings-changed', handler);
  }, []);

  // ── Mount: create the chart once ──
  React.useEffect(() => {
    if (!contRef.current || !data || data.length === 0) return;
    if (initializedRef.current) return;
    initializedRef.current = true;

    const chart = f3.createChart(contRef.current, data);
    chartRef.current = chart;

    // Measure the longest name to set card width dynamically
    const { cardW, spacingX } = calcCardDimensions(data, settings);

    chart
      .setTransitionTime(600)
      .setOrientationVertical()
      .setCardXSpacing(spacingX)
      .setCardYSpacing(settings.cardSpacingY)
      .setSortChildrenFunction((a, b) => {
        const aYear = parseInt(a.data.birth_year, 10) || 9999;
        const bYear = parseInt(b.data.birth_year, 10) || 9999;
        return aYear - bYear;
      });

    const card = chart.setCardHtml();
    cardRef.current = card;

    card
      .setStyle('rect')
      .setCardDim({ width: cardW, height: settings.cardHeight, height_auto: true })
      .setMiniTree(true)
      .setOnHoverPathToMain()
      .setCardInnerHtmlCreator((d) => buildCardHtml(d, theme, selectedId, settings, memberGroupMap, activeGroupFilter))
      .setOnCardClick((e, d) => {
        try {
          chart.updateMainId(d.data.id);
          chart.updateTree({});
        } catch (err) {
          console.error('[FamilyChartPane] click updateTree error:', err.message);
        }
        if (onPersonClick) onPersonClick(d.data.id);
      });

    try {
      chart.updateTree({ initial: true });
    } catch (err) {
      console.error('[FamilyChartPane] initial updateTree error:', err.message);
    }

    // Fix z-index: ensure HTML cards render above SVG connector lines
    applyLineStyles(contRef.current, settings);

    return () => {
      initializedRef.current = false;
      chartRef.current = null;
      cardRef.current = null;
      if (contRef.current) contRef.current.innerHTML = '';
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Update data when members/relations change ──
  // `settings` is deliberately NOT a dependency. It is only read for the
  // applyLineStyles call below, and the card/theme effect underneath already
  // re-runs on every settings change and reapplies line styles itself. Listing
  // it here meant one settings tweak fired BOTH effects: this one first with
  // the chart's default 600ms transition, then the card effect immediately with
  // transition_time 0 — a redundant full re-layout that visibly snapped, and
  // ~60 of them across a single slider drag.
  React.useEffect(() => {
    if (!chartRef.current || !data || data.length === 0) return;
    if (!initializedRef.current) return;
    try {
      chartRef.current.updateData(data);
      // tree_position defaults to 'fit', which recomputes a whole-tree fit and
      // overwrites the user's zoom/pan on EVERY update. 'inherit' is a no-op in
      // the library's view renderer, so the camera stays where they left it.
      // The mount call above deliberately keeps the default: initial:true wants
      // the first fit.
      chartRef.current.updateTree({ initial: false, tree_position: 'inherit' });
    } catch (err) {
      console.error('[FamilyChartPane] updateTree error (data inconsistency):', err.message);
    }
    applyLineStyles(contRef.current, settings);

    // How is each person related to the interviewee? The library can compute a
    // kinship path across the relationship graph, which is both sex-aware and
    // generation-aware ("great-granddaughter", not "grandchild"). Anchored to
    // the SUBJECT deliberately: clicking a card re-roots the tree, and that
    // must not change what "related to the interviewee" means.
    //
    // Only as good as the graph — anyone not connected to the subject yields
    // no label, and the caller falls back to the stored relation.
    if (onKinships) {
      try {
        const subject = (data || []).find(d => d.data?._isSubject) || (data || [])[0];
        if (subject) {
          onKinships(chartRef.current.calculateKinships(subject.id, { show_in_law: true }) || {});
        }
      } catch (err) {
        console.error('[FamilyChartPane] kinship calculation skipped:', err.message);
        onKinships({});
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  // Tracks what the selection was on the previous run, so this effect can tell
  // "the user picked a different person" (centre on them) from "a colour
  // changed" (leave the camera alone).
  const prevSelectedRef = React.useRef(selectedId);

  // ── Update card rendering when theme, selectedId, or settings change ──
  React.useEffect(() => {
    if (!cardRef.current || !chartRef.current) return;
    if (!initializedRef.current) return;
    const { cardW, spacingX } = calcCardDimensions(data || [], settings);

    cardRef.current
      .setCardDim({ width: cardW, height: settings.cardHeight, height_auto: true })
      .setCardInnerHtmlCreator((d) => buildCardHtml(d, theme, selectedId, settings, memberGroupMap, activeGroupFilter));
    chartRef.current
      .setCardXSpacing(spacingX)
      .setCardYSpacing(settings.cardSpacingY);
    // Picking a person in the left-hand list should bring them into view — that
    // is the point of the list. Everything else here (theme, colours, card
    // sizing) is cosmetic and must NOT move the camera.
    //
    // 'main_to_middle' centres the main card, so the person is made main first.
    // Passing the CURRENT zoom as `scale` keeps the user's zoom level; without
    // it the library resets to 1. Previously this path used the library's
    // default ('fit'), which re-fitted the whole tree on every cosmetic change
    // as well — that was the pan/zoom loss.
    const selectionChanged = prevSelectedRef.current !== selectedId;
    prevSelectedRef.current = selectedId;

    let viewProps = { initial: false, transition_time: 0, tree_position: 'inherit' };
    if (selectionChanged && selectedId) {
      const datum = (data || []).find(d => String(d.id) === String(selectedId));
      if (datum) {
        let scale;
        try {
          const svgEl = contRef.current?.querySelector('svg');
          scale = svgEl && f3.handlers?.getCurrentZoom?.(svgEl)?.k;
        } catch { /* fall through to the library default */ }
        try { chartRef.current.updateMainId(datum.id); } catch { /* keep going; centring is best-effort */ }
        viewProps = {
          initial: false,
          transition_time: 400,
          tree_position: 'main_to_middle',
          ...(scale ? { scale } : {}),
        };
      }
    }

    try {
      chartRef.current.updateTree(viewProps);
    } catch (err) {
      console.error('[FamilyChartPane] settings updateTree error:', err.message);
    }
    applyLineStyles(contRef.current, settings);
  }, [theme, selectedId, settings, memberGroupMap, activeGroupFilter, data]);

  const containerStyle = React.useMemo(() => ({
    width: '100%',
    height: '100%',
    '--background-color': theme.card || '#1a1a2e',
    '--text-color': theme.text || '#fff',
    '--female-color': settings.femaleColor,
    '--male-color': settings.maleColor,
    '--genderless-color': theme.border || '#555',
  }), [theme, settings]);

  return (
    <div
      ref={contRef}
      className="f3"
      style={containerStyle}
    />
  );
}


/**
 * Apply connector line styles and fix z-index so lines don't show through cards.
 */
function applyLineStyles(container, settings) {
  if (!container) return;
  // Fix z-index: push SVG below HTML card overlay
  const svg = container.querySelector('svg.main_svg');
  if (svg) svg.style.zIndex = '1';
  const htmlSvg = container.querySelector('#htmlSvg');
  if (htmlSvg) htmlSvg.style.zIndex = '2';
  // Apply line color and width to all connector paths
  const links = container.querySelectorAll('.link');
  links.forEach(link => {
    link.setAttribute('stroke', settings.lineColor || '#888888');
    link.setAttribute('stroke-width', settings.lineWidth || 1);
    link.style.opacity = '1';
  });
  // Also ensure card containers have solid backgrounds (not transparent)
  const cards = container.querySelectorAll('.card_cont .card');
  cards.forEach(card => {
    card.style.position = 'relative';
    card.style.zIndex = '3';
  });
}

/**
 * Measure the longest name in the data and compute card width + spacing.
 * Uses a hidden canvas for accurate pixel-width measurement.
 */
function calcCardDimensions(data, settings) {
  // Measure text with canvas for accuracy
  let maxTextWidth = 0;
  try {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const fontSizePx = settings.nameFontSize * 16; // rem → px (assuming 16px root)
    ctx.font = `700 ${fontSizePx}px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif`;
    data.forEach(d => {
      const name = d.data?.name || '';
      if (name) {
        const w = ctx.measureText(name).width;
        if (w > maxTextWidth) maxTextWidth = w;
      }
    });
  } catch {
    // Fallback: estimate ~7px per char
    const longest = data.reduce((max, d) => Math.max(max, (d.data?.name || '').length), 0);
    maxTextWidth = longest * 7;
  }

  // Card width = text width + padding on both sides
  const padding = (settings.cardPadding + 2) * 2;
  const cardW = Math.max(settings.cardWidth, Math.ceil(maxTextWidth + padding + 10));
  // Spacing must be at least cardWidth + a gap so cards don't touch
  const spacingX = Math.max(settings.cardSpacingX, cardW + 30);

  return { cardW, spacingX };
}


/**
 * Build HTML string for a family-chart card.
 */
function buildCardHtml(treeDatum, theme, selectedId, settings, memberGroupMap = {}, activeGroupFilter = null) {
  const d = treeDatum.data;
  const personData = d.data;
  const isSubject = personData._isSubject;
  const id = d.id;
  const isSelected = String(selectedId) === String(id);

  const name = personData.name || 'Unknown';
  const relation = personData.relation || 'other';
  const fate = personData.fate || 'unknown';
  const birthYear = personData.birth_year || '';
  const deathYear = personData.death_year || '';

  const relCfg = isSubject
    ? { label: 'Subject', color: settings.subjectHighlight }
    : (RELATION_CONFIG[relation] || RELATION_CONFIG.other);
  const fateCfg = FATE_CONFIG[fate] || FATE_CONFIG.unknown;

  // Apply per-fate color overrides
  let fateColor = fateCfg.color;
  let fateBg = fateCfg.bg;
  const fateOverride = { survived: settings.fateSurvivedColor, killed: settings.fateKilledColor, emigrated: settings.fateEmigratedColor }[fate];
  const fateOverrideHex = safeHex(fateOverride, null);
  if (fateOverrideHex) { fateColor = fateOverrideHex; fateBg = fateOverrideHex + '18'; }

  // Group membership
  const personGroups = memberGroupMap[id] || [];
  // Group filter: DIM everyone outside the selected group rather than hiding
  // them, so the shape of the family stays legible and you can still see how
  // the highlighted people connect to the rest of the tree.
  const dimmed = !!activeGroupFilter && !personGroups.some(g => String(g.id) === String(activeGroupFilter));
  const primaryGroupColor = personGroups.length > 0 ? safeHex(personGroups[0].color, null) : null;

  const highlightHex = safeHex(settings.subjectHighlight, DEFAULT_SETTINGS.subjectHighlight);
  const relColorHex = safeHex(relCfg.color, '#888888');
  const borderColor = isSelected ? highlightHex : (primaryGroupColor ? `${primaryGroupColor}aa` : `${relColorHex}88`);
  const bgColor = isSelected ? `${highlightHex}20` : (primaryGroupColor ? `${primaryGroupColor}0c` : (theme.card || '#222'));

  const dates = settings.showDates && (birthYear || deathYear)
    ? `<div style="font-size:${settings.dateFontSize}rem;color:${theme.textSub || '#888'};margin-top:2px;">${escapeHtml(birthYear || '?')}${deathYear ? ' - ' + escapeHtml(deathYear) : ''}</div>`
    : '';

  const fateBadge = settings.showFateBadge
    ? `<div style="font-size:${settings.fateFontSize}rem;font-weight:700;color:${safeHex(fateColor, '#888888')};background:${safeHex(fateBg, '#88888818') || '#88888818'};padding:1px 6px;border-radius:999px;display:inline-block;margin-top:3px;">${fateCfg.label}</div>`
    : '';

  const gender = personData.gender || '';
  const genderColor = gender === 'F'
    ? safeHex(settings.femaleColor, DEFAULT_SETTINGS.femaleColor)
    : safeHex(settings.maleColor, DEFAULT_SETTINGS.maleColor);
  const genderPill = gender
    ? `<span style="font-size:${settings.relationFontSize}rem;font-weight:800;color:${genderColor};background:${genderColor}18;border:1px solid ${genderColor}44;padding:0px 5px;border-radius:999px;display:inline-flex;align-items:center;line-height:1.4;">${gender}</span>`
    : '';

  const relationLabel = settings.showRelationLabel
    ? `<div style="display:flex;align-items:center;gap:4px;margin-top:1px;"><span style="font-size:${settings.relationFontSize}rem;font-weight:700;letter-spacing:0.05em;text-transform:uppercase;color:${relColorHex};">${escapeHtml(relCfg.label)}</span>${genderPill}</div>`
    : (genderPill ? `<div style="margin-top:1px;">${genderPill}</div>` : '');

  return `
    <div dir="auto" style="
      opacity:${dimmed ? '0.28' : '1'};
      transition:opacity 0.18s;
      padding:${settings.cardPadding}px ${settings.cardPadding + 2}px;
      text-align:start;
      border:1.5px solid ${borderColor};
      border-radius:${settings.cardRadius}px;
      background:${bgColor};
      color:${theme.text || '#fff'};
      cursor:pointer;
      line-height:1.3;
    ">
      <div dir="auto" style="font-size:${settings.nameFontSize}rem;font-weight:700;white-space:nowrap;">
        ${escapeHtml(name)}
      </div>
      ${relationLabel}
      ${dates}
      ${fateBadge}
      ${personGroups.length > 0 ? `<div style="display:flex;flex-wrap:wrap;gap:2px;margin-top:3px;">${[...personGroups].sort((a, b) => (a.name || '').localeCompare(b.name || '')).map(g => { const gc = safeHex(g.color, '#888888'); return `<span style="font-size:0.56rem;font-weight:700;color:${gc};background:${gc}18;border:1px solid ${gc}33;padding:0px 4px;border-radius:6px;white-space:nowrap;">${escapeHtml(g.name)}</span>`; }).join('')}</div>` : ''}
    </div>
  `;
}

function escapeHtml(str) {
  if (!str) return '';
  return String(str)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

/**
 * Return `value` only if it is a hex colour, otherwise `fallback`.
 *
 * Every colour below is concatenated into a style="" attribute in an HTML
 * string that goes straight to innerHTML, and most are then suffixed with an
 * alpha pair (`${colour}18`). Two things follow:
 *
 *  - A value containing a quote or semicolon would break out of the attribute.
 *    Group colours come from the database and survive a JSON restore, so that
 *    is a stored injection path, not a hypothetical one.
 *  - The alpha-suffix trick only works on 6-digit hex. Anything else ("red",
 *    "rgb(...)") silently produced garbage like "rgb(1,2,3)18".
 *
 * Normalising to 6-digit hex fixes both at once. Shorthand is expanded and any
 * existing alpha is dropped, so the suffix stays valid.
 */
function safeHex(value, fallback) {
  const v = String(value || '').trim();
  let m = /^#([0-9a-fA-F]{3})$/.exec(v);
  if (m) return '#' + m[1].split('').map(c => c + c).join('');
  m = /^#([0-9a-fA-F]{6})(?:[0-9a-fA-F]{2})?$/.exec(v);
  if (m) return '#' + m[1];
  return fallback;
}


// ── Exported settings modal component ──
export function GenogramSettingsModal({ theme, onClose }) {
  const [settings, setSettings] = React.useState(loadSettings);

  const update = (key, value) => {
    setSettings(prev => {
      const next = { ...prev, [key]: value };
      saveSettings(next);
      return next;
    });
  };

  const reset = () => {
    saveSettings(DEFAULT_SETTINGS);
    setSettings({ ...DEFAULT_SETTINGS });
  };

  const sliderRow = (label, key, min, max, step = 1, unit = '') => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
      <label htmlFor={`genogram-${key}`} style={{ fontSize: '0.68rem', fontWeight: 600, color: theme.text, minWidth: '100px', flexShrink: 0 }}>{label}</label>
      <input id={`genogram-${key}`} type="range" min={min} max={max} step={step} value={settings[key]}
        onChange={e => update(key, parseFloat(e.target.value))}
        style={{ flex: 1, accentColor: theme.glossaryAccent }} />
      <span style={{ fontSize: '0.62rem', color: theme.textSub, minWidth: '40px', textAlign: 'right' }}>{settings[key]}{unit}</span>
    </div>
  );

  // Only the three fate overrides default to null; every other colour setting
  // has a real default, so the fallback below never fires for them.
  const colorRow = (label, key) => {
    const fateDefault = FATE_COLOR_DEFAULTS[key];
    const isOverridden = !!settings[key];
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
        <label htmlFor={`genogram-${key}`} style={{ fontSize: '0.68rem', fontWeight: 600, color: theme.text, minWidth: '100px', flexShrink: 0 }}>{label}</label>
        <input id={`genogram-${key}`} type="color" value={settings[key] || fateDefault || '#888888'}
          onChange={e => update(key, e.target.value)}
          style={{ width: '28px', height: '22px', border: `1px solid ${theme.border}`, borderRadius: '4px', cursor: 'pointer', background: 'none' }} />
        <span style={{ fontSize: '0.58rem', color: theme.textSub, fontFamily: 'monospace', flex: 1 }}>{settings[key] || 'default'}</span>
        {fateDefault && isOverridden && (
          <button onClick={() => update(key, null)} title={`Reset ${label} to default`} aria-label={`Reset ${label} to default`}
            style={{ padding: '1px 7px', fontSize: '0.56rem', fontWeight: 700, borderRadius: '6px', cursor: 'pointer',
              color: theme.textSub, background: 'transparent', border: `1px solid ${theme.border}`, flexShrink: 0 }}>
            Reset
          </button>
        )}
      </div>
    );
  };

  const toggleRow = (label, key) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
      <label htmlFor={`genogram-${key}`} style={{ fontSize: '0.68rem', fontWeight: 600, color: theme.text, minWidth: '100px', flexShrink: 0 }}>{label}</label>
      <button id={`genogram-${key}`} onClick={() => update(key, !settings[key])} aria-pressed={settings[key]}
        style={{ padding: '2px 10px', fontSize: '0.62rem', fontWeight: 700, borderRadius: '10px', cursor: 'pointer',
          background: settings[key] ? `${theme.glossaryAccent}18` : 'transparent',
          color: settings[key] ? theme.glossaryAccent : theme.textSub,
          border: `1px solid ${settings[key] ? theme.glossaryAccent : theme.border}` }}>
        {settings[key] ? 'ON' : 'OFF'}
      </button>
    </div>
  );

  const sectionLabel = (text) => (
    <div style={{ fontSize: '0.56rem', fontWeight: 700, color: theme.textSub, letterSpacing: '0.08em', textTransform: 'uppercase', marginTop: '12px', marginBottom: '6px', borderBottom: `1px solid ${theme.border}`, paddingBottom: '4px' }}>
      {text}
    </div>
  );

  return (
    <div style={{
      position: 'absolute', top: '48px', right: '10px', zIndex: Z.dropdown,
      width: '320px', maxHeight: '70vh', overflowY: 'auto',
      background: theme.card, border: `1px solid ${theme.border}`, borderRadius: '12px',
      boxShadow: '0 8px 32px rgba(0,0,0,0.25)', padding: '14px',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
        <span style={{ fontSize: '0.72rem', fontWeight: 700, color: theme.text }}>Card Settings</span>
        <div style={{ display: 'flex', gap: '6px' }}>
          <button onClick={reset}
            style={{ padding: '3px 8px', fontSize: '0.58rem', fontWeight: 700, borderRadius: '6px', cursor: 'pointer',
              color: '#f59e0b', background: '#f59e0b14', border: '1px solid #f59e0b33' }}>
            Reset
          </button>
          <button onClick={onClose}
            style={{ padding: '3px 8px', fontSize: '0.62rem', fontWeight: 700, borderRadius: '6px', cursor: 'pointer',
              color: theme.textSub, background: 'transparent', border: `1px solid ${theme.border}` }}>
            Close
          </button>
        </div>
      </div>

      {sectionLabel('Card Dimensions')}
      {sliderRow('Width', 'cardWidth', 120, 300, 5, 'px')}
      {sliderRow('Height', 'cardHeight', 50, 150, 5, 'px')}
      {sliderRow('Spacing X', 'cardSpacingX', 150, 400, 10, 'px')}
      {sliderRow('Spacing Y', 'cardSpacingY', 100, 300, 10, 'px')}
      {sliderRow('Radius', 'cardRadius', 0, 20, 1, 'px')}
      {sliderRow('Padding', 'cardPadding', 4, 16, 1, 'px')}

      {sectionLabel('Font Sizes')}
      {sliderRow('Name', 'nameFontSize', 0.5, 1.2, 0.02, 'rem')}
      {sliderRow('Relation', 'relationFontSize', 0.3, 0.8, 0.02, 'rem')}
      {sliderRow('Dates', 'dateFontSize', 0.3, 0.8, 0.02, 'rem')}
      {sliderRow('Fate badge', 'fateFontSize', 0.3, 0.7, 0.02, 'rem')}

      {sectionLabel('Visibility')}
      {toggleRow('Dates', 'showDates')}
      {toggleRow('Fate badge', 'showFateBadge')}
      {toggleRow('Relation label', 'showRelationLabel')}

      {sectionLabel('Connector Lines')}
      {colorRow('Line color', 'lineColor')}
      {sliderRow('Line width', 'lineWidth', 0.5, 4, 0.5, 'px')}

      {sectionLabel('Colors')}
      {colorRow('Subject', 'subjectHighlight')}
      {colorRow('Male bar', 'maleColor')}
      {colorRow('Female bar', 'femaleColor')}
      {colorRow('Survived', 'fateSurvivedColor')}
      {colorRow('Murdered', 'fateKilledColor')}
      {colorRow('Emigrated', 'fateEmigratedColor')}
    </div>
  );
}
